import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ChevronDown, ChevronUp, History, Loader2, RefreshCw, Search } from 'lucide-react';
import { TONES } from '../../lib/eventJourney';
import {
    KINDS, KIND_KEYS, actorName, dayLabel, describeRow, groupEntries, isAutomatic, kindOf, sourceMeta, timeLabel,
} from '../../lib/eventActivityLog';
import { createEventGuestsApi } from '../../lib/eventGuestsApi';
import { errorText } from '../../lib/apiErrors';
import { GuestIcon, TonePill } from './guests/guestUi';

const PAGE = 50;
const SOURCE_FILTERS = [['any', 'By hand or automatic'], ['manual', 'By hand'], ['auto', 'Automatic, form or face match']];

function FilterMenu({ label, value, options, onChange, testId }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const close = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);
    const current = options.find(([key]) => key === value) || options[0];
    const active = value !== options[0][0];
    return (
        <span ref={ref} className="relative" onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }}>
            <button type="button" data-testid={testId} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}
                className={`inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 text-[13px] font-medium ${active ? 'border-stone-300 bg-stone-100' : 'border-stone-200 bg-white'}`}>
                {label}: {current[1]}<ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            {open && (
                <ul role="menu" aria-label={label} className="absolute left-0 top-full z-30 mt-1 min-w-[220px] rounded-lg border border-stone-200 bg-white p-1 shadow-lg">
                    {options.map(([key, text]) => (
                        <li key={key} role="none">
                            <button type="button" role="menuitemradio" aria-checked={key === value} data-testid={`${testId}-${key}`} onClick={() => { setOpen(false); onChange(key); }}
                                className="flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-left text-[13px] hover:bg-stone-100">{text}{key === value && <span aria-hidden="true">✓</span>}</button>
                        </li>
                    ))}
                </ul>
            )}
        </span>
    );
}

/**
 * The event's log viewer (OA-2, OA-4, 1013-105): everything that happened on the event, newest first, from
 * `GET /events/{id}/history` (one `lifecycle_events` row per change, tagged with this event). Kind chips with
 * counts, Who and How filters and a search all go to the server; "Load older activity" pages with the
 * server's cursor. A row opens to show the stored fields; a guest's name opens their drawer on the Guests tab.
 */
export default function EventActivityTab({ eventId, getAuthHeader, onOpenGuest, timeZone }) {
    const api = useMemo(() => createEventGuestsApi(getAuthHeader), [getAuthHeader]);
    const [filters, setFilters] = useState({ kind: 'all', actor: 'all', source: 'any' });
    const [search, setSearch] = useState('');
    const [q, setQ] = useState('');
    const [items, setItems] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [counts, setCounts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const [open, setOpen] = useState({});
    const [actors, setActors] = useState({});
    const token = useRef(0);

    useEffect(() => { const timer = setTimeout(() => setQ(search.trim()), 300); return () => clearTimeout(timer); }, [search]);

    const params = useCallback((extra = {}) => ({
        kind: filters.kind === 'all' ? undefined : filters.kind,
        actor_id: filters.actor === 'all' ? undefined : filters.actor,
        source: filters.source === 'any' ? undefined : filters.source,
        q: q || undefined, limit: PAGE, ...extra,
    }), [filters, q]);

    const remember = useCallback((rows) => setActors((current) => {
        const next = { ...current };
        rows.forEach((row) => { if (row.actor?.kind !== 'system' && row.actor?.id) next[row.actor.id] = row.actor.name || 'Someone'; });
        return next;
    }), []);

    const load = useCallback(async () => {
        const mine = ++token.current;
        setLoading(true); setError('');
        try {
            const res = await api.getHistory(eventId, params());
            if (mine !== token.current) return;
            const rows = res?.items || [];
            setItems(rows); setCursor(res?.next_cursor || null); setCounts(res?.counts_by_kind || null); remember(rows);
        } catch (err) {
            if (mine === token.current) setError(errorText(err, 'Could not load the event activity'));
        } finally {
            if (mine === token.current) setLoading(false);
        }
    }, [api, eventId, params, remember]);
    useEffect(() => { load(); }, [load]);

    const loadMore = async () => {
        if (!cursor || loadingMore) return;
        const mine = token.current;
        setLoadingMore(true);
        try {
            const res = await api.getHistory(eventId, params({ cursor }));
            if (mine !== token.current) return;
            const rows = res?.items || [];
            setItems((current) => current.concat(rows)); setCursor(res?.next_cursor || null); remember(rows);
        } catch (err) {
            setError(errorText(err, 'Could not load older activity'));
        } finally { setLoadingMore(false); }
    };

    const entries = useMemo(() => groupEntries(items), [items]);
    const days = useMemo(() => {
        const out = [];
        entries.forEach((entry) => {
            const label = dayLabel(entry.first.changed_at, timeZone);
            const last = out[out.length - 1];
            if (last && last.label === label) last.rows.push(entry); else out.push({ label, rows: [entry] });
        });
        return out;
    }, [entries, timeZone]);

    const people = new Set(items.filter((row) => row.actor?.kind !== 'system' && row.actor?.id).map((row) => row.actor.id)).size;
    const automatic = items.filter(isAutomatic).length;
    const total = counts?.all ?? items.length;
    const filtered = filters.kind !== 'all' || filters.actor !== 'all' || filters.source !== 'any' || Boolean(q);
    const whoOptions = [['all', 'Everyone'], ['system', 'Automatic (Nurture)'], ...Object.entries(actors)];
    const set = (patch) => setFilters((current) => ({ ...current, ...patch }));

    return (
        <section data-testid="event-activity-tab" className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="max-w-2xl">
                    <h2 className="text-lg font-semibold text-stone-900">Event activity</h2>
                    <p className="text-sm text-stone-500">Everything that happened on this event, newest first: who moved a guest, checked someone in, started, ended or rescheduled it. Guest entries also appear in each contact&apos;s activity log.</p>
                </div>
                <div className="flex gap-2" data-testid="event-activity-stats">
                    {[['Entries', total], ['People', people], ['Automatic', automatic]].map(([label, value]) => (
                        <div key={label} className="min-w-[88px] rounded-lg border border-stone-200 bg-white px-3 py-2">
                            <span className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500">{label}</span>
                            <span className="block text-lg font-semibold tabular-nums text-stone-900">{value}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <label className="relative">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400" aria-hidden="true" />
                    <input aria-label="Search guest or person" placeholder="Search guest or person" value={search} onChange={(e) => setSearch(e.target.value)}
                        className="h-8 w-56 rounded-lg border border-stone-200 bg-white pl-8 pr-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                </label>
                <div className="flex flex-wrap items-center gap-0.5 rounded-lg bg-stone-100 p-0.5" role="group" aria-label="Kind">
                    {[['all', 'All'], ...KIND_KEYS.map((key) => [key, KINDS[key].label])].map(([key, label]) => (
                        <button key={key} type="button" aria-pressed={filters.kind === key} data-testid={`activity-kind-${key}`} onClick={() => set({ kind: key })}
                            className={`inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-[12.5px] font-medium ${filters.kind === key ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-600'}`}>
                            {label}<span className="text-[11px] tabular-nums text-stone-400">{counts ? (key === 'all' ? counts.all : counts[key]) ?? 0 : ''}</span>
                        </button>
                    ))}
                </div>
                <FilterMenu label="Who" value={filters.actor} options={whoOptions} onChange={(actor) => set({ actor })} testId="activity-who" />
                <FilterMenu label="How" value={filters.source} options={SOURCE_FILTERS} onChange={(source) => set({ source })} testId="activity-how" />
                <span className="flex-1" />
                <span className="text-xs text-stone-500" data-testid="activity-count">{filtered && counts ? `${items.length} shown of ${total} entries` : `${total} entries`}</span>
            </div>

            <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
                {loading && items.length === 0 && <p data-testid="activity-loading" className="flex items-center gap-2 p-6 text-sm text-stone-500"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Loading activity…</p>}
                {error && (
                    <div role="alert" data-testid="activity-error" className="flex items-center gap-3 border-b border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}<button type="button" onClick={load} className="inline-flex items-center gap-1 rounded border border-red-300 px-2 py-1 text-xs"><RefreshCw className="h-3 w-3" aria-hidden="true" />Retry</button>
                    </div>
                )}
                {days.map((day) => (
                    <div key={day.label}>
                        <div className="border-b border-stone-100 bg-stone-50 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-stone-500">{day.label}</div>
                        <ul>
                            {day.rows.map((entry) => {
                                const row = entry.first;
                                const kind = KINDS[kindOf(row)];
                                const t = TONES[kind.tone];
                                const src = sourceMeta(row);
                                const { verb, tail } = describeRow(row);
                                const expanded = Boolean(open[entry.key]);
                                const toggle = () => setOpen((current) => ({ ...current, [entry.key]: !current[entry.key] }));
                                const guest = row.contact_name || '';
                                return (
                                    <li key={entry.key} data-testid="activity-row" data-kind={kindOf(row)} onClick={(event) => { if (!event.target.closest('button')) toggle(); }}
                                        className="flex cursor-pointer items-start gap-3 border-b border-stone-100 px-4 py-3 last:border-b-0 hover:bg-stone-50">
                                        <span className="w-11 shrink-0 pt-1 text-xs tabular-nums text-stone-400">{timeLabel(row.changed_at, timeZone)}</span>
                                        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border" style={{ background: t.bg, color: t.fg, borderColor: t.bd }}><GuestIcon name={kind.icon} className="h-3.5 w-3.5" /></span>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm text-stone-800">
                                                <span className="font-semibold">{actorName(row)}</span> {verb}{' '}
                                                {guest && (row.contact_id && onOpenGuest
                                                    ? <button type="button" data-testid="activity-guest" onClick={() => onOpenGuest(row.contact_id)} className="font-semibold underline decoration-stone-300 underline-offset-2 hover:decoration-stone-600">{guest}</button>
                                                    : <span className="font-semibold">{guest}</span>)}{tail}
                                            </p>
                                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                                {entry.changes.slice(0, 3).map((change) => (change.from || change.to) && (
                                                    <span key={`${change.field}-${change.to}`} className="inline-flex items-center gap-1">
                                                        {change.from && <TonePill className="opacity-75">{change.from}</TonePill>}
                                                        {change.from && change.to && <ArrowRight className="h-3 w-3 text-stone-400" aria-hidden="true" />}
                                                        {change.to && <TonePill tone="blue">{change.to}</TonePill>}
                                                    </span>
                                                ))}
                                                <TonePill tone={src.tone} icon={src.icon} data-testid="activity-source">{src.label}</TonePill>
                                                {row.reason && <span className="text-xs text-stone-500">“{row.reason}”</span>}
                                            </div>
                                            {expanded && (
                                                <dl data-testid="activity-details" className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg bg-stone-50 px-3 py-2 text-xs">
                                                    {[
                                                        ['log', 'lifecycle_events'], ['event_type', row.event_type], ['subject', `${row.entity_type || 'event'}${guest ? ` · ${guest}` : ''}`],
                                                        ['causer', `${row.actor?.kind || 'user'} · ${row.actor?.name || row.actor?.id || '—'}`],
                                                        ['field', entry.items.map((r) => r.field).filter(Boolean).join(', ') || '—'], ['operation_id', row.operation_id || '—'],
                                                    ].map(([k, v]) => <React.Fragment key={k}><dt className="font-mono text-stone-500">{k}</dt><dd className="break-all font-mono text-stone-800">{v}</dd></React.Fragment>)}
                                                </dl>
                                            )}
                                        </div>
                                        <button type="button" aria-expanded={expanded} aria-label={expanded ? 'Hide details' : 'Show details'} onClick={toggle} className="shrink-0 rounded-md p-1 text-stone-400 hover:bg-stone-100">
                                            {expanded ? <ChevronUp className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}
                {!loading && !error && items.length === 0 && (
                    <div data-testid="activity-empty" className="flex flex-col items-center gap-1 px-4 py-12 text-center">
                        <History className="h-6 w-6 text-stone-300" aria-hidden="true" />
                        <span className="text-sm font-medium text-stone-700">{filtered ? 'No activity matches these filters' : 'No activity yet'}</span>
                        <span className="text-xs text-stone-500">{filtered ? 'Clear a filter to see more of this event\'s log.' : 'Moves, check-ins, RSVPs and event changes appear here as they happen.'}</span>
                    </div>
                )}
                {cursor && (
                    <button type="button" data-testid="activity-load-more" disabled={loadingMore} onClick={loadMore} className="w-full border-t border-stone-100 py-3 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-60">
                        {loadingMore ? 'Loading…' : 'Load older activity'}
                    </button>
                )}
            </div>
        </section>
    );
}
